import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AnimatePresence } from 'framer-motion';
import { useEventLearnableSnippet } from 'src/analytics/useEventLearnableSnippet';
import { AnalyticsDataMediaType } from 'src/types/googleAnalyticsEvents';
import { AutoPlayContext } from 'src/context/AutoPlayContext';
import { useKeyboardShortcut } from 'src/hooks';
import * as S from './styles';

export { PlayBlobContainer, PlayButton, PlayIconContainer } from './styles';

const msgs = defineMessages({
  seoTextButton: {
    id: 'video_player_alt_text',
  },
});

interface VideoPlayerProps {
  CustomPlayButton?: FC<{ onClick: () => void }>;
  className?: string;
  src: string;
  playOnKeyboardShortcut: boolean;
  autoPlay?: boolean;
  preload?: 'auto' | 'metadata' | 'none';

  /**
   * A callback to run when the video starts playing
   */
  onPlay?: (details?: { duration: number }) => void;
  onEnded?: () => void;
  onCanPlay?: () => void;
  onClick?: (event: React.MouseEvent) => void;
  title: string;
}

export const VideoPlayer = ({
  CustomPlayButton,
  className,
  src,
  playOnKeyboardShortcut,
  onPlay,
  onEnded,
  onCanPlay,
  onClick,
  autoPlay = true,
  preload = 'auto',
  title,
}: VideoPlayerProps) => {
  const intl = useIntl();
  const [showPlayButton, setShowPlayButton] = useState(true);
  const vidRef = useRef<HTMLVideoElement>(null);

  const { autoPlayRestricted, handleAutoplayError } = useContext(AutoPlayContext);

  const eventLearnableSnippet = useEventLearnableSnippet(AnalyticsDataMediaType.video, {
    title,
    url: src,
  });

  const handlePlay = useCallback(() => {
    const { current } = vidRef;
    /* istanbul ignore next: untested branch of code, please test */
    if (!current) return;
    current
      .play()
      .then(() => {
        window.dataLayer.push(eventLearnableSnippet);
        setShowPlayButton(false);
        /* istanbul ignore next: untested branch of code, please test */
        if (onPlay) {
          onPlay({ duration: current.duration });
        }
      })
      .catch(error => {
        current.load();
        setShowPlayButton(true);
        handleAutoplayError(error);
      });
  }, [eventLearnableSnippet, handleAutoplayError, onPlay]);

  useKeyboardShortcut('r', playOnKeyboardShortcut ? handlePlay : undefined);

  useEffect(() => {
    if (autoPlay) {
      handlePlay();
    }
    // eslint-disable-next-line no-warning-comments
    // TODO: check does it need autoPlay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlePlay]);

  // Set correct play state when video finishes playing
  useEffect(() => {
    // Ignore this function due to unmocked JSDom video methods
    /* istanbul ignore next */
    const handleEnd = () => {
      setShowPlayButton(true);
    };
    // Ignore this function due to unmocked JSDom video methods
    /* istanbul ignore next */
    const handleEnded = () => {
      if (onEnded) {
        onEnded();
      }
      handleEnd();
    };
    const { current } = vidRef;
    current && current.addEventListener('ended', handleEnded);
    current && current.addEventListener('pause', handleEnd);
    return () => {
      current && current.removeEventListener('ended', handleEnded);
      current && current.removeEventListener('pause', handleEnd);
    };
    // eslint-disable-next-line no-warning-comments
    // TODO: check does it need onEnded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vidRef, setShowPlayButton]);

  return (
    <S.Container onClick={onClick} className={className}>
      <S.Video
        // @ts-ignore
        ref={vidRef}
        data-testid="videoPlayer"
        src={`${src}#t=0.1`}
        onCanPlay={onCanPlay}
        autoPlay={autoPlay ? !autoPlayRestricted : false}
        playsInline
        preload={preload}
        poster={`data:image/svg+xml,%3Csvg width='256'
      height='256' viewBox='0 0 256 256' fill='none'
      xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='256' height='256'
      fill='%23DCDCDC'/%3E%3C/svg%3E`}
      />
      {CustomPlayButton ? (
        <CustomPlayButton
          aria-label={intl.formatMessage(msgs.seoTextButton)}
          data-testid="playButton"
          key="playButton"
          onClick={handlePlay}
        />
      ) : (
        <AnimatePresence>
          {showPlayButton && (
            <S.PlayButton
              onClick={handlePlay}
              key="playButton"
              data-testid="playButton"
              aria-label={intl.formatMessage(msgs.seoTextButton)}
            >
              <S.PlayBlobContainer>
                <S.PlayBlob />
              </S.PlayBlobContainer>
              <S.PlayIconContainer>
                <S.Icon name="play" />
              </S.PlayIconContainer>
            </S.PlayButton>
          )}
        </AnimatePresence>
      )}
    </S.Container>
  );
};
